import React, { useEffect, useRef, useState } from "react";
import { useStorageSource } from "@rebasepro/app";
import { cls, defaultBorderMixin } from "@rebasepro/ui";

/**
 * The face of a person, wherever the panel names one.
 *
 * Every list in this panel is a list of people — the queue, a vacancy's
 * applicants, a shortlist — and until now every one of them drew a person as a
 * line of text. Fifty rows of names is a spreadsheet: nothing on screen is
 * anchored to a human being, and finding somebody you spoke to yesterday means
 * reading rather than recognising.
 *
 * ── Why this is not just an `<img>` ──────────────────────────────────
 *
 * `talents.photo_url` holds a *storage key*, not a URL (see drizzle/0045 for
 * why: a `media.licdn.com` link expires and would rot silently). A key has to
 * be exchanged for a short-lived, file-scoped download URL before a browser can
 * fetch it, which is a round trip — so it cannot be done in the middle of a
 * render, and it must be cached or a 400-row list makes 400 identical requests.
 *
 * `TalentOverview` did put `photo_url` straight into an `<img src>`. That was a
 * broken image on every candidate who has a photo, which is why nobody noticed
 * it was there: it renders the key as a relative path and 404s.
 *
 * ── Why initials, and not a grey circle ──────────────────────────────
 *
 * 50 of 402 candidates have a photo. `collections/talents.ts` disables the card
 * view over exactly this — "300 identical grey placeholders is worse than no
 * card view" — and that reasoning holds for a silhouette icon too. Initials on
 * a tint derived from the name are *different for each person*, so the column
 * stays scannable at 12% photo coverage, and the same person keeps the same
 * colour everywhere they appear.
 */

/**
 * Resolved download URLs, keyed by storage key, shared by every avatar mounted.
 *
 * The promise is cached rather than the value, so ten rows for the same person
 * (queue, cover, applicants) make one request between them rather than ten.
 *
 * **Only successes, and only for a while.** The signed URL carries a download
 * token the server lets expire after 300 seconds, so an entry older than that
 * is a link that 403s: it is kept for four minutes and then asked for again.
 * A failure is not kept at all — it used to be cached as `null` for the life
 * of the tab, which turned one timeout or one blip in the storage API into
 * initials for that person until a reload. The next mount retries instead;
 * the in-flight promise is still shared, so a list of 400 rows retrying makes
 * one request per person, not 400.
 */
const PHOTO_URL_TTL_MS = 4 * 60 * 1000;

interface PhotoEntry {
    pending: Promise<string | null>;
    /** What `pending` answered, once it has. */
    url?: string | null;
    expires: number;
}

const resolved = new Map<string, PhotoEntry>();

type StorageSourceLike = { getSignedUrl: (key: string) => Promise<{ url?: string | null }> };

function photoUrl(storage: StorageSourceLike, key: string): Promise<string | null> {
    const hit = resolved.get(key);
    if (hit && hit.expires > Date.now()) return hit.pending;
    const entry: PhotoEntry = { expires: Date.now() + PHOTO_URL_TTL_MS, pending: Promise.resolve(null) };
    entry.pending = storage.getSignedUrl(key)
        .then(config => config?.url ?? null)
        .catch(() => null)
        .then(url => {
            entry.url = url;
            // Forget a miss once it has answered everybody waiting on it —
            // unless a newer request has already replaced this entry.
            if (!url && resolved.get(key) === entry) resolved.delete(key);
            return url;
        });
    resolved.set(key, entry);
    return entry.pending;
}

/**
 * Drop a URL that resolved but would not load (an expired token, a deleted
 * object), so the next avatar to mount for this person signs a fresh one.
 * Only the entry that produced `url` is dropped: a newer one is left alone.
 */
function forgetPhotoUrl(key: string, url: string) {
    const hit = resolved.get(key);
    if (hit && hit.url === url) resolved.delete(key);
}

/**
 * Words that are not the name.
 *
 * Spanish names carry particles between the parts that identify somebody, and
 * "María de la Cruz Hernández" initialled naively is "MH" — right by luck — while
 * "Ana de los Santos" becomes "AS" only if `los` is skipped. These are dropped
 * before the first and last word are chosen.
 */
const PARTICLES = new Set(["de", "del", "la", "las", "los", "y", "e", "da", "do", "dos", "van", "von", "der"]);

/**
 * One or two letters standing for a person.
 *
 * Falls back through what the panel actually holds: a full name, else an email
 * (several views use the address when the ficha has no name), else a neutral
 * mark — never an empty circle, which reads as a rendering fault rather than as
 * a person we know little about.
 */
export function initialsOf(name: string | null | undefined): string {
    const raw = (name ?? "").trim();
    if (!raw) return "·";

    // An email is a name here often enough to be worth handling: take the local
    // part, and its first letter, rather than initialling "juan.perez@x.com"
    // into "J@".
    if (!raw.includes(" ") && raw.includes("@")) {
        const local = raw.split("@")[0].replace(/[^\p{L}\p{N}]+/gu, " ").trim();
        return (local.charAt(0) || "·").toUpperCase();
    }

    const words = raw.split(/\s+/)
        .filter(word => !PARTICLES.has(word.toLowerCase()))
        .filter(word => /\p{L}/u.test(word));
    if (words.length === 0) return raw.charAt(0).toUpperCase();
    if (words.length === 1) return words[0].charAt(0).toUpperCase();
    return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
}

/**
 * The field a person with no photo sits on.
 *
 * Four hues — forest, petrol, clay and a neutral slate — each drawn three ways:
 * a pale wash to sit on, a hairline of the same hue to close it, and the hue
 * deepened for the letters. Picked by a hash of the name, so the same candidate
 * is the same mark in the queue, on their cover and in a vacancy's applicants,
 * which is the whole point of showing one.
 *
 * **Pale, not solid.** Most of this roster has no photograph, so this mark is
 * the common case rather than the exception — and 400 saturated discs down a
 * column make the *absence* of a photo the loudest thing on the screen. Quiet
 * keeps the names the thing you read while the colour still does its one job.
 *
 * **Far apart, not faithful.** The first set was forest, petrol, leaf and clay
 * — the product's palette exactly, and useless washed out, because forest and
 * leaf become the same pale green and two thirds of the column looked alike.
 *
 * Kept in step by hand with `src/common/person.ts` on the site: two builds, no
 * module between them, and the same person must not show different letters or a
 * different colour in the panel and in the app.
 */
const FIELDS: [string, string, string][] = [
    // wash, rule, ink
    ["#e5eee8", "#bdd2c4", "#17452f"], // forest
    ["#e1ecf0", "#b5cfd8", "#1d4a55"], // petrol
    ["#f1e9df", "#dbc8ae", "#4a3b2a"], // clay
    ["#e9eaec", "#c7cbd2", "#39404a"]  // slate
];

export interface PersonField {
    wash: string;
    rule: string;
    ink: string;
}

export function fieldOf(name: string | null | undefined): PersonField {
    const raw = (name ?? "").trim().toLowerCase();
    let hash = 0;
    for (let i = 0; i < raw.length; i++) {
        hash = (hash * 31 + raw.charCodeAt(i)) % 100000;
    }
    const [wash, rule, ink] = FIELDS[hash % FIELDS.length];
    return { wash, rule, ink };
}

export interface PersonAvatarProps {
    /** What we call this person. Also what the initials and the tint come from. */
    name?: string | null;
    /** `talents.photo_url` — a storage key, not a URL. Absent means initials. */
    photoKey?: string | null;
    /** Edge length in pixels. The type scales with it. */
    size?: number;
    className?: string;
}

/**
 * A person, as a circle.
 *
 * Round rather than square on purpose, and the same rule the public site
 * follows (DESIGN.md, "People are round; organisations are square"): a
 * candidate's mark and an employer's logo tile sit three rows apart in this
 * panel, and the shape is what tells them apart before the text does.
 *
 * Decorative by declaration: every call site has the person's name in the very
 * next element, and "foto de Ana Ruiz, Ana Ruiz" on 400 rows is noise for a
 * screen reader rather than information.
 */
export function PersonAvatar({ name, photoKey, size = 36, className }: PersonAvatarProps) {
    const storage = useStorageSource() as unknown as StorageSourceLike;
    const [url, setUrl] = useState<string | null>(null);
    // One fresh signature per mount after a load error, and no more: an object
    // that is really gone would otherwise sign, fail, sign, fail for ever.
    const retried = useRef(false);
    const currentKey = useRef(photoKey);
    currentKey.current = photoKey;

    useEffect(() => {
        retried.current = false;
        if (!photoKey || !storage?.getSignedUrl) {
            setUrl(null);
            return;
        }
        let live = true;
        photoUrl(storage, photoKey).then(next => { if (live) setUrl(next); });
        return () => { live = false; };
    }, [photoKey, storage]);

    const onPhotoError = () => {
        const failed = url;
        setUrl(null);
        if (!photoKey || !failed) return;
        forgetPhotoUrl(photoKey, failed);
        if (retried.current || !storage?.getSignedUrl) return;
        retried.current = true;
        const key = photoKey;
        photoUrl(storage, key).then(next => {
            if (next && next !== failed && currentKey.current === key) setUrl(next);
        });
    };

    const box: React.CSSProperties = {
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "9999px"
    };

    if (url) {
        return (
            <img
                src={url}
                alt=""
                aria-hidden="true"
                // Lazy because a queue renders every candidate it has: the
                // photos below the fold are bytes nobody asked for. No
                // `?width=` transform — `sharp` is an optional dependency of the
                // server package and a transform it cannot perform answers 500,
                // which would break every photo rather than merely serve a large
                // one.
                loading="lazy"
                decoding="async"
                style={box}
                className={cls("object-cover shrink-0 border", defaultBorderMixin, className)}
                // A key that resolved to a URL can still fail to load — a
                // deleted object, an expired token on a long-open tab. Falling
                // back to initials keeps the row's shape rather than leaving a
                // broken-image glyph beside somebody's name, and the URL is
                // evicted and signed again once (see `onPhotoError`).
                onError={onPhotoError}
            />
        );
    }

    const field = fieldOf(name);
    return (
        <span
            aria-hidden="true"
            style={{
                ...box,
                background: field.wash,
                color: field.ink,
                // An inset shadow rather than a border, so the ring costs no
                // layout: `box` already fixes the edge length, and a 1px border
                // on top of it would make the circle 2px wider than the photo
                // it stands in for.
                boxShadow: `inset 0 0 0 1px ${field.rule}`,
                fontSize: Math.round(size * 0.36)
            }}
            className={cls(
                "shrink-0 inline-flex items-center justify-center select-none leading-none",
                className
            )}
        >
            {initialsOf(name)}
        </span>
    );
}
